export function formatDate(dateString) {
  if (!dateString) return "-";

  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear().toString();

  return `${hours}:${minutes}h | ${day}/${month}/${year}`;
}

export function formatDateWithoutTime(dateString) {
  if (!dateString) return "-";

  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear().toString();

  return `${day}/${month}/${year}`;
}

const calendarParts = (value?: string | null): string | null => {
  if (!value) return null;

  const iso = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso.slice(0, 10);

  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
};

export function toDateInputValue(value?: string | null): string {
  return calendarParts(value) ?? "";
}

export function formatCalendarDate(value?: string | null): string {
  const parts = calendarParts(value);
  if (!parts) return "-";

  const [year, month, day] = parts.split("-");
  return `${day}/${month}/${year}`;
}

export function formatDateTime(value?: string | null): string {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return `${day}/${month}/${date.getFullYear()} às ${hours}:${minutes}`;
}

export function todayInputValue(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${now.getFullYear()}-${month}-${day}`;
}

const DAY_IN_MS = 24 * 60 * 60 * 1000;

export function dayBeforeCalendarDate(value?: string | null): string {
  const day = toDateInputValue(value);
  if (!day) return "";

  const date = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return "";

  return new Date(date.getTime() - DAY_IN_MS).toISOString().slice(0, 10);
}

export function formatClockTime(value: Date, timeZone?: string): string {
  const options: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };

  try {
    return value.toLocaleTimeString("pt-BR", { ...options, timeZone });
  } catch {
    return value.toLocaleTimeString("pt-BR", options);
  }
}

export function formatShortTime(
  value?: string | null,
  timeZone?: string
): string {
  if (!value) return "--:--";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--:--";

  try {
    return date.toLocaleTimeString("pt-BR", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}

export function formatLongDate(value: Date, timeZone?: string): string {
  const options: Intl.DateTimeFormatOptions = {
    weekday: "long",
    day: "numeric",
    month: "long",
  };

  let label: string;
  try {
    label = value.toLocaleDateString("pt-BR", { ...options, timeZone });
  } catch {
    label = value.toLocaleDateString("pt-BR", options);
  }

  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function formatZonedDateTime(
  value?: string | null,
  timeZone?: string
): string {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const dateOptions: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  };
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
  };

  try {
    return `${date.toLocaleDateString("pt-BR", { ...dateOptions, timeZone })} às ${date.toLocaleTimeString("pt-BR", { ...timeOptions, timeZone })}`;
  } catch {
    return formatDateTime(value);
  }
}
